import { useEffect, useMemo, useState } from 'react';
import { Modal, Platform, Pressable, View, useWindowDimensions } from 'react-native';

import { Sidebar } from './Sidebar';
import { BottomTabs } from './BottomTabs';
import { TopBar } from './TopBar';
import { NotificacionesPanel } from './NotificacionesPanel';
import { JaulasVencidasToast } from './JaulasVencidasToast';
import { ReportarBugButton } from '@/components/ReportarBugButton';
import { CatFishPattern } from '@/components/ui';
import { colors } from '@/theme/tokens';
import { ROUTES } from '@/navigation/routes';
import { useFirestoreCollection } from '@/hooks/useFirestoreCollection';
import { notificacionesPara } from '@/services/firebase/notificaciones';
import { useAuth } from '@/stores/authStore';

interface Props {
  current: string;
  onNavigate: (name: string) => void;
  children: React.ReactNode;
}

const STORAGE_KEY = 'clinicat:sidebar-collapsed';

export function AppShell({ current, onNavigate, children }: Props) {
  const { width, height } = useWindowDimensions();
  const wide = width >= 920;
  const perfil = useAuth((s) => s.perfil);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notifsOpen, setNotifsOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (Platform.OS !== 'web') return false;
    try {
      return globalThis.localStorage?.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    try {
      globalThis.localStorage?.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      /* noop */
    }
  }, [collapsed]);

  const notifQuery = useMemo(() => (perfil ? notificacionesPara(perfil.rol) : null), [perfil]);
  const { data: notifs } = useFirestoreCollection(notifQuery);
  const unread = notifs.filter((n) => !n.leida).length;

  const route = ROUTES.find((r) => r.name === current);
  const title = route?.label ?? 'CliniCAT';

  const shellStyle =
    Platform.OS === 'web'
      ? {
          height,
          flexDirection: wide ? ('row' as const) : ('column' as const),
          backgroundColor: colors.crema[50],
          overflow: 'hidden' as const,
        }
      : {
          flex: 1,
          flexDirection: wide ? ('row' as const) : ('column' as const),
          backgroundColor: colors.crema[50],
        };

  return (
    <View style={shellStyle}>
      {wide ? (
        <Sidebar
          current={current}
          collapsed={collapsed}
          onToggleCollapse={() => setCollapsed((c) => !c)}
          onNavigate={onNavigate}
        />
      ) : null}

      <View style={{ flex: 1, minHeight: 0, minWidth: 0 }}>
        <TopBar
          title={title}
          onMenu={() => setDrawerOpen(true)}
          onNotifications={() => setNotifsOpen(true)}
          unreadAlerts={unread}
        />
        <View style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          <CatFishPattern variant="crema" opacity={0.4} tileSize={340} />
          <View style={{ flex: 1, minHeight: 0 }}>{children}</View>
        </View>
        {!wide ? <BottomTabs current={current} onNavigate={onNavigate} /> : null}
      </View>

      {!wide ? (
        <Modal visible={drawerOpen} animationType="fade" transparent onRequestClose={() => setDrawerOpen(false)}>
          <View style={{ flex: 1, flexDirection: 'row' }}>
            <Sidebar
              current={current}
              onNavigate={(n) => {
                onNavigate(n);
                setDrawerOpen(false);
              }}
            />
            <Pressable
              style={{ flex: 1, backgroundColor: 'rgba(15, 23, 42, 0.5)' }}
              onPress={() => setDrawerOpen(false)}
            />
          </View>
        </Modal>
      ) : null}

      <NotificacionesPanel visible={notifsOpen} onClose={() => setNotifsOpen(false)} />

      <JaulasVencidasToast onVer={() => onNavigate('Jaulas')} />

      <ReportarBugButton />
    </View>
  );
}
